import { inject, Injectable } from '@angular/core';
import { assert, isDefined, mapToObject } from '@sama/js-core';
import { switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { AnswersByName } from '../../../team-apis/otterdam/answers/answer';
import {
  CalculateScoreRequest,
  CalculateScoreRequestObjectless,
  OutputMap,
  OutputMapObjectless,
} from '../../../team-apis/otterdam/calculate-score-request';
import { CalculateScoreResponseObjectless } from '../../../team-apis/otterdam/calculate-score-response';
import { isAnnotationOutput } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AbstractResource } from '../../http/abstract-resource';
import { AnnotationResource } from './annotation-resource.service';

@Injectable()
export class TrainingTaskResourceService extends AbstractResource {
  private tokenManagementService = inject(TokenManagementService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private annotationResource = inject(AnnotationResource);

  calculateScore$() {
    const request: CalculateScoreRequest = {
      settings: { outputMap: this.getOutputMap() },
    };

    return this.annotationResource.calculateTrainingScore$(request);
  }

  calculateScoreObjectless$(answers: AnswersByName) {
    const trainingAnswers =
      this.taskWorkspaceStore.state().task._extras.training?.answers;
    assert(isDefined(trainingAnswers), 'Training answers should be defined.');

    const body: CalculateScoreRequestObjectless = {
      expected: trainingAnswers,
      actual: answers,
      settings: { outputMap: this.getOutputMapObjectless() },
    };

    return this.tokenManagementService.getToken$('access_token').pipe(
      switchMap((token) =>
        this.request$<CalculateScoreResponseObjectless>(
          'POST',
          calculateTrainingScorePathObjectless(),
          {
            body,
            headers: { Authorization: 'Bearer ' + token },
            customErrorMessage: 'Error calculating training score.',
          },
        ),
      ),
    );
  }

  private getOutputMap(): OutputMap {
    // Send all outputs to the backend, even the nested ones.
    // This facilitates tests and there isn't much gain from filtering it.
    const { outputs } = this.taskWorkspaceStore.state().project;
    return mapToObject(outputs, (output) => [
      output.id,
      {
        type: output.type,
        id: output.id,
        name: output.name,
        thresholdValues: isAnnotationOutput(output)
          ? output.meta.threshold_values
          : undefined,
      },
    ]);
  }

  private getOutputMapObjectless(): OutputMapObjectless {
    // Send all outputs to the backend, even the nested ones.
    // This facilitates tests and there isn't much gain from filtering it.
    const { outputs } = this.taskWorkspaceStore.state().project;
    return mapToObject(outputs, (output) => [
      output.name,
      {
        type: output.type,
        id: output.id,
        thresholdValues: isAnnotationOutput(output)
          ? output.meta.threshold_values
          : undefined,
      },
    ]);
  }
}

export function calculateTrainingScorePathObjectless() {
  return `${environment.annotationStoreServiceBaseUrl}/annotations/diff`;
}
